import { ReactNode, useState } from 'react';
import { LucideIcon, Plus, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../ui/command';
import LoadingRing from '../LoadingRing';
import { cn } from '../../../lib/utils';
import {
    linkedRowClass,
    linkedChipClass,
    linkedNameClass,
    linkedMetaClass,
} from '../linkedItemStyles';
import { useToast } from '../../../hooks/use-toast';

export interface LinkPickerText {
    empty: string;
    add: string;
    search: string;
    noMatching: string;
    remove: string;
    loadFailed: string;
}

interface LinkPickerProps<T extends { _id: string }> {
    value: T[];
    onChange: (items: T[]) => void;
    load: () => Promise<T[]>;
    label: (item: T) => string;
    meta?: (item: T) => string | undefined;
    icon: LucideIcon;
    text: LinkPickerText;
    actions?: ReactNode;
    compact?: boolean;
}

export default function LinkPicker<T extends { _id: string }>({
    value,
    onChange,
    load,
    label,
    meta,
    icon: Icon,
    text,
    actions,
    compact = false,
}: LinkPickerProps<T>) {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [all, setAll] = useState<T[] | null>(null);

    const openPopover = async (next: boolean) => {
        setOpen(next);
        if (!next || all) return;
        setLoading(true);
        try {
            setAll(await load());
        } catch {
            toast({ title: text.loadFailed, variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const selected = new Set(value.map((item) => item._id));
    const available = (all ?? []).filter((item) => !selected.has(item._id));

    const trigger = compact ? (
        <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 border-control px-3 text-xs"
        >
            <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            {text.add}
        </Button>
    ) : (
        <Button type="button" variant="outline" size="sm" className="flex-1">
            <Plus className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            {text.add}
        </Button>
    );

    const popover = (
        <Popover open={open} onOpenChange={openPopover}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent
                align="start"
                className={
                    compact
                        ? 'w-72 p-0'
                        : 'w-[var(--radix-popover-trigger-width)] p-0'
                }
            >
                <Command>
                    <CommandInput placeholder={text.search} />
                    <CommandList onWheel={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="flex justify-center py-6">
                                <LoadingRing size="small" />
                            </div>
                        ) : (
                            <>
                                <CommandEmpty>{text.noMatching}</CommandEmpty>
                                <CommandGroup>
                                    {available.map((item) => (
                                        <CommandItem
                                            key={item._id}
                                            value={`${label(item)} ${item._id}`}
                                            onSelect={() => {
                                                onChange([...value, item]);
                                                setOpen(false);
                                            }}
                                        >
                                            <span className="truncate text-sm font-medium">
                                                {label(item)}
                                            </span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );

    const remove = (id: string) =>
        onChange(value.filter((item) => item._id !== id));

    return (
        <div className={cn('space-y-2', compact && 'space-y-1.5')}>
            {!compact && value.length === 0 && (
                <p className="text-sm font-medium text-subtle-foreground">
                    {text.empty}
                </p>
            )}
            {value.length > 0 && (
                <div
                    className={
                        compact
                            ? 'flex flex-wrap items-center gap-1.5'
                            : 'space-y-2'
                    }
                >
                    {value.map((item) => (
                        <span
                            key={item._id}
                            className={
                                compact ? linkedChipClass : linkedRowClass
                            }
                        >
                            <Icon
                                className={cn(
                                    'shrink-0 text-subtle-foreground',
                                    compact ? 'h-3 w-3' : 'h-4 w-4'
                                )}
                                aria-hidden="true"
                            />
                            <span
                                className={
                                    compact ? 'truncate' : linkedNameClass
                                }
                            >
                                {label(item)}
                            </span>
                            {!compact && meta?.(item) && (
                                <span className={linkedMetaClass}>
                                    {meta(item)}
                                </span>
                            )}
                            <button
                                type="button"
                                aria-label={text.remove}
                                className={cn(
                                    'shrink-0 text-subtle-foreground hover:text-destructive',
                                    compact ? 'ml-0.5' : 'p-1'
                                )}
                                onClick={() => remove(item._id)}
                            >
                                <X
                                    className={
                                        compact ? 'h-3 w-3' : 'h-3.5 w-3.5'
                                    }
                                    aria-hidden="true"
                                />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
                {popover}
                {actions}
            </div>
        </div>
    );
}
